'use client';

import { useState } from 'react';
import { 
  FileCheck, 
  Plus, 
  Edit2, 
  Trash2, 
  Upload,
  Loader2,
  Filter,
  X,
  Search,
  Link2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  useEvidence,
  useCreateEvidence,
  useUpdateEvidence,
  useDeleteEvidence,
  type Evidence,
  type EvidenceType,
} from '@/hooks/use-evidence';
import { useStories } from '@/hooks/use-artifacts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface EvidencePanelProps {
  projectId: string;
  projectName?: string;
}

const EVIDENCE_TYPES: { value: EvidenceType; label: string }[] = [
  { value: 'TEST_RESULTS', label: 'Test Results' },
  { value: 'SECURITY_SCAN', label: 'Security Scan' },
  { value: 'PERFORMANCE_TEST', label: 'Performance Test' },
  { value: 'CODE_REVIEW', label: 'Code Review' },
  { value: 'MANUAL_QA', label: 'Manual QA' },
  { value: 'USER_ACCEPTANCE', label: 'User Acceptance' },
  { value: 'COMPLIANCE_CHECK', label: 'Compliance Check' },
];

export function EvidencePanel({ projectId, projectName = 'Project' }: EvidencePanelProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingEvidence, setEditingEvidence] = useState<Evidence | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<EvidenceType | undefined>();
  
  const { toast } = useToast();
  const { data: evidence, isLoading } = useEvidence(projectId, { type: typeFilter });
  const createMutation = useCreateEvidence(projectId);
  const updateMutation = useUpdateEvidence(projectId);
  const deleteMutation = useDeleteEvidence(projectId);

  const handleCreate = async (data: Partial<Evidence> & { type: EvidenceType; title: string }) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Evidence created', description: 'Evidence has been created successfully.' });
      setShowCreateDialog(false);
    } catch (error) {
      toast({ 
        title: 'Creation failed', 
        description: error instanceof Error ? error.message : 'Failed to create evidence',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (evidenceId: string, data: Partial<Evidence>) => {
    try {
      await updateMutation.mutateAsync({ evidenceId, data });
      toast({ title: 'Evidence updated', description: 'Evidence has been updated successfully.' });
      setEditingEvidence(null);
    } catch (error) {
      toast({ 
        title: 'Update failed', 
        description: error instanceof Error ? error.message : 'Failed to update evidence',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (evidenceId: string) => {
    try {
      await deleteMutation.mutateAsync(evidenceId);
      toast({ title: 'Evidence deleted', description: 'Evidence has been deleted successfully.' });
      setShowDeleteConfirm(null);
    } catch (error) {
      toast({ 
        title: 'Deletion failed', 
        description: error instanceof Error ? error.message : 'Failed to delete evidence',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Evidence</h1>
            <p className="text-sm text-gray-600 mt-1">Manage evidence items for {projectName}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? undefined : v as EvidenceType)}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EVIDENCE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Evidence
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!evidence || evidence.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <FileCheck className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Evidence</h3>
            <p className="text-gray-600 mb-6">Add evidence items to track test results, security scans, and more.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Evidence
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {evidence.map((item) => {
              const typeLabel = EVIDENCE_TYPES.find(t => t.value === item.type)?.label || item.type;
              
              return (
                <Card key={item.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{item.title}</CardTitle>
                          <Badge variant="outline">{typeLabel}</Badge>
                          {item.status === 'ARCHIVED' && (
                            <Badge variant="secondary">Archived</Badge>
                          )}
                        </div>
                        {item.description && (
                          <CardDescription>{item.description}</CardDescription>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingEvidence(item)}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {item.storagePath && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FileCheck className="w-4 h-4" />
                        <span>File: {item.storagePath}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <EvidenceDialog
        isOpen={showCreateDialog || editingEvidence !== null}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingEvidence(null);
        }}
        onSave={editingEvidence 
          ? (data) => handleUpdate(editingEvidence.id, data)
          : handleCreate
        }
        evidence={editingEvidence}
        isLoading={createMutation.isPending || updateMutation.isPending}
        projectId={projectId}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Evidence</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this evidence item? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface EvidenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Evidence> & { type: EvidenceType; title: string }) => Promise<void>;
  evidence?: Evidence | null;
  isLoading?: boolean;
  projectId: string;
}

function EvidenceDialog({ isOpen, onClose, onSave, evidence, isLoading, projectId }: EvidenceDialogProps) {
  const { data: stories } = useStories(projectId);
  const [storySearch, setStorySearch] = useState('');
  
  const [formData, setFormData] = useState<Partial<Evidence> & { type: EvidenceType; title: string }>({
    type: evidence?.type || 'TEST_RESULTS',
    title: evidence?.title || '',
    description: evidence?.description || '',
    storyId: evidence?.storyId || undefined,
  });
  const [file, setFile] = useState<File | null>(null);
  
  // Filter stories by search
  const filteredStories = stories?.filter(story => 
    story.title?.toLowerCase().includes(storySearch.toLowerCase()) ||
    story.description?.toLowerCase().includes(storySearch.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{evidence ? 'Edit Evidence' : 'Add Evidence'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Type *</label>
            <Select
              value={formData.type}
              onValueChange={(v) => setFormData({ ...formData, type: v as EvidenceType })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVIDENCE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Upload File</label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="flex-1"
              />
              {file && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Story Linking */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Linked Story (Optional)</label>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search stories..."
                  value={storySearch}
                  onChange={(e) => setStorySearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={formData.storyId || ''}
                onValueChange={(value) => setFormData({ ...formData, storyId: value || undefined })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a story (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {filteredStories.map((story) => (
                    <SelectItem key={story.id} value={story.id}>
                      {story.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.storyId && (
                <div className="flex items-center gap-2 pt-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {stories?.find(s => s.id === formData.storyId)?.title}
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, storyId: undefined })}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
